import { Box, BoxProps, Container, Flex, Grid, Heading } from '@chakra-ui/react';
import { faArrowRight } from '@fortawesome/pro-solid-svg-icons';
import { CMS } from '@waitroom/models';
import * as React from 'react';
import CmsButton from '../../../../cms/components/Button/CmsButton';
import CmsIcon from '../../../../cms/components/Icon/CmsIcon';
import VideoOrImage from '../../../../cms/components/VideoOrImage/VideoOrImage';
import { secondaryTextColor } from '../../../../cms/utils/base';
import { isLinkDefined } from '../../../../cms/utils/sanity';
import { CDN_IMAGES_URL } from '../../../config';
import { Icon } from '../../Icon/Icon';
import Markdown from '../../Markdown/Markdown';

const noImage2 = `${CDN_IMAGES_URL}/fallback-2.png`;
const Hero = ({
  variant,
  heading,
  content,
  image,
  video,
  button,
  link,
  fullHeight,
  icon,
  footnote,
  ...rest
}: CMS.Hero & BoxProps): React.ReactElement | null => {
  const isFirst = rest['data-index' as keyof typeof rest] === 0;
  const isExtended = variant === 1;
  const isIcon = variant === 2;
  const bp = isExtended ? 'xl' : 'lg';
  const hasButton = isLinkDefined(button);
  const hasLink = isLinkDefined(link);
  const spacingProps = isFirst
    ? {
        pt: isExtended ? { base: 6, sm: 8, [bp]: 0 } : [8, 12, 14],
        pb: isExtended ? { base: 12, sm: 16, [bp]: 0 } : rest.pt || rest.py,
      }
    : { pt: rest.pt ?? rest.py, pb: rest.pb ?? rest.py };
  const imageSize: [number, number] = isExtended ? [880, 660] : [600, 450];

  const contentUi = (
    <Flex
      direction="column"
      w="full"
      order={{ base: 1, [bp]: 0 }}
      rowGap={[6, 8]}
      py={{ base: 0, [bp]: isExtended ? 16 : 0 }}
      align={isIcon ? 'center' : undefined}
      textAlign={isIcon ? 'center' : undefined}
    >
      {heading && (
        <Heading
          as="h2"
          fontSize={{
            base: '2rem',
            sm: '2.5rem',
            md: '2.75rem',
            '2xl': '3.5rem',
          }}
          lineHeight="shorter"
        >
          {heading}
        </Heading>
      )}
      {content && <Markdown fontSize={['md', 'lg', 'xl']}>{content}</Markdown>}
      <div>
        <Flex
          w="full"
          direction="row"
          rowGap={1}
          columnGap={3}
          align="center"
          justify={isIcon ? 'center' : undefined}
          wrap="wrap"
        >
          {hasButton && (
            <CmsButton
              data={button}
              isButton
              layoutBg={rest.bgColor}
              size={['def', 'md', 'lg']}
              minW={['150px', '150px']}
            >
              {button.text}
            </CmsButton>
          )}
          {hasLink && (
            <CmsButton
              data={link}
              layoutBg={rest.bgColor}
              size={['sm', 'md', 'lg']}
              fontSize={['sm', 'md', 'lg']}
              px={[4, 6]}
              rightIcon={<Icon icon={faArrowRight} />}
            >
              {link.text}
            </CmsButton>
          )}
        </Flex>
        {footnote && (
          <Markdown textAlign="center" color={secondaryTextColor(rest.bgColor)} mt={4}>
            {footnote}
          </Markdown>
        )}
      </div>
    </Flex>
  );

  return (
    <Box overflow="hidden" {...rest} py={0} pt={0} pb={0} data-testid="hero">
      <Container
        maxW={isIcon ? 'container.xl' : 'container.2xl'}
        pt={isExtended && !fullHeight ? { base: 0, [bp]: 6, '2xl': 10 } : 0}
      >
        <Flex
          minH={fullHeight ? '100vh' : 0}
          mt={isFirst ? ['-64px', null, null, '-80px'] : 0}
          pt={isFirst ? ['64px', null, null, '80px'] : 0}
          align={isExtended ? { [bp]: 'flex-end' } : 'center'}
        >
          {isIcon ? (
            <Box w="full" {...spacingProps}>
              {icon ? (
                <Flex justify="center" my={8}>
                  <CmsIcon icon={icon} index={0} />
                </Flex>
              ) : null}
              {contentUi}
            </Box>
          ) : (
            <Grid
              w="100%"
              templateColumns={{
                base: 'repeat(1, 1fr)',
                [bp]: isExtended ? '38% 57%' : '47.5% 47.5%',
              }}
              columnGap={{ [bp]: '5%' }}
              rowGap={[6, 8]}
              alignItems="center"
              {...spacingProps}
            >
              {contentUi}
              {isExtended ? (
                <Box
                  position="relative"
                  order={{ base: 0, [bp]: 1 }}
                  alignSelf="flex-end"
                  overflow="visible"
                  h={{ base: 'auto', [bp]: '500px', '2xl': '620px' }}
                >
                  <Box position={{ [bp]: 'absolute' }} h="100%" bottom={0} mx={-3}>
                    <VideoOrImage
                      video={video}
                      image={image}
                      size={imageSize}
                      fallbackSrc={noImage2}
                      w={{ base: '100%', [bp]: 'auto' }}
                      h={{ base: 'auto', [bp]: '100%' }}
                      maxW={{ [bp]: 'none' }}
                    />
                  </Box>
                </Box>
              ) : (
                <Box order={{ base: 0, xl: 1 }}>
                  <VideoOrImage
                    video={video}
                    image={image}
                    size={imageSize}
                    fallbackSrc={noImage2}
                    w="100%"
                  />
                </Box>
              )}
            </Grid>
          )}
        </Flex>
      </Container>
    </Box>
  );
};
export default Hero;
